// ProcessTimeline.js - Portugal
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Golden Visa',
      stages: [
        { step: 'Prepare Investment & Documents', duration: '1-3 months', description: 'Secure investment, gather documents, obtain NIF and bank account' },
        { step: 'Submit Application', duration: '1-2 weeks', description: 'Submit complete application with all supporting documents' },
        { step: 'Biometrics Appointment', duration: '2-4 weeks', description: 'Attend biometrics appointment at Portuguese consulate or in Portugal' },
        { step: 'Application Processing', duration: '6-12 months', description: 'SEF processes application and verifies investment' },
        { step: 'Residence Permit Issued', duration: '1-2 weeks', description: 'Receive 2-year residence permit card' },
        { step: 'Renewal (after 2 years)', duration: '2-4 months', description: 'Renew for 3 years, then permanent residency after 5 years' }
      ],
      totalTime: '8-18 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'D7 Visa (Passive Income)',
      stages: [
        { step: 'Gather Income Documentation', duration: '2-4 weeks', description: 'Compile proof of passive income, bank statements, accommodation' },
        { step: 'Consulate Application', duration: '1-2 weeks', description: 'Submit D7 visa application at Portuguese consulate in home country' },
        { step: 'Application Processing', duration: '2-4 months', description: 'Consulate reviews application and supporting documents' },
        { step: 'Visa Approval & Travel', duration: '1-2 weeks', description: 'Receive D7 visa and travel to Portugal within 4 months' },
        { step: 'SEF Appointment', duration: '1-3 months', description: 'Schedule and attend SEF appointment in Portugal for residence card' },
        { step: 'Residence Card Issued', duration: '2-4 weeks', description: 'Receive 2-year residence permit card' }
      ],
      totalTime: '6-12 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Permanent Residency (after 5 years)',
      stages: [
        { step: 'Verify Eligibility', duration: '1-2 weeks', description: 'Confirm 5 years of legal residency and compliance with requirements' },
        { step: 'Gather Documents', duration: '2-4 weeks', description: 'Collect updated documents, tax records, criminal record certificate' },
        { step: 'Submit PR Application', duration: '1 week', description: 'Submit permanent residency application to SEF' },
        { step: 'Application Processing', duration: '6-12 months', description: 'SEF processes permanent residency application' },
        { step: 'Permanent Residence Granted', duration: '1-2 weeks', description: 'Receive permanent residence permit (valid indefinitely)' }
      ],
      totalTime: '8-14 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Standard Processing Timelines
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Understanding the typical timeline for each pathway helps you plan your residency journey effectively.
          </p>
        </motion.div>

        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-[#202356] mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>
                      <div className="bg-[#F5F8FA] p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-[#202356]">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-[#2542D4] bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              Processing Time Factors
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Completeness and accuracy of documentation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Investment verification and due diligence</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>SEF workload and appointment availability</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Background checks and verification</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-[#202356] mb-3">
              How We Help
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Guide investment selection and setup</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Ensure complete and accurate documentation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Coordinate with lawyers and investment advisors</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Monitor application status and respond to requests</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
